import React from 'react';
import ReactDOM from 'react-dom';

import FeatureCard from './components/feature-card';
import Profile from './components/profile';
import getContributionsForEntity from './services/getContributionsForEntity';
import sortRowsByField from './services/sortRowsByField';
import SearchField from './components/search-field';

/**
 * App component.
 */
const appElement = document.getElementById('campaign-finance-interactive');

class CampaignFinance extends React.PureComponent {
  constructor(props) {
    super(props);

    this.handleMemberClick = this.handleMemberClick.bind(this);
    this.handleCompanyClick = this.handleCompanyClick.bind(this);
    this.handleMemberTableSortClick =
      this.handleMemberTableSortClick.bind(this);
    this.handleCompanyTableSortClick =
      this.handleCompanyTableSortClick.bind(this);
    this.onSearchSelectMember = this.onSearchSelectMember.bind(this);
    this.onSearchSelectCompany = this.onSearchSelectCompany.bind(this);
    this.state = {
      activeCompany: {},
      activeCompanyContribs: [],
      activeMember: {},
      activeMemberContribs: [],
      companies: [],
      companiesMap: new Map(),
      contributions: [],
      error: false,
      featuredCompanies: [],
      featuredMembers: [],
      loading: true,
      members: [],
      membersMap: new Map(),
    };
  }

  /**
   * Handles fetching API data after the component is mounted.
   */
  componentDidMount() {
    this.getMapData('/wp-json/khn/v1/campaign-finance-companies', 'companies');
    this.getMapData('/wp-json/khn/v1/campaign-finance-members', 'members');
    this.getStaticData(
      '/wp-json/khn/v1/campaign-finance-contributions',
      'contributions'
    );
    this.getStaticData(
      '/wp-json/khn/v1/campaign-finance-featured-companies',
      'featuredCompanies'
    );
    this.getStaticData(
      '/wp-json/khn/v1/campaign-finance-featured-members',
      'featuredMembers'
    );
  }

  /**
   * An onSelect function to be called when the search field selects a
   * suggested component
   */
  onSearchSelectMember(evt, member) {
    this.setState({
      activeMember: member.suggestion,
      activeMemberContribs: this.getMemberContribs(member.suggestion.id),
    });
  }

  /**
   * An onSelect function to be called when the search field selects a
   * suggested component
   */
  onSearchSelectCompany(evt, member) {
    this.setState({
      activeCompany: member.suggestion,
      activeCompanyContribs: this.getCompanyContribs(member.suggestion.id),
    });
  }

  /**
   * A generic function to fetch data from the API and add it to state directly.
   *
   * @param {string} endpoint - The endpoint to fetch.
   * @param {string} property - The property name in state to update.
   */
  getStaticData(endpoint, property) {
    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        this.setState({ [property]: data }, this.updateLoadingStatus);
      })
      .catch((error) => {
        this.setState({ error });
      });
  }

  /**
   * A generic function to fetch data from the API and add it to a Map.
   *
   * @param {string} endpoint - The endpoint to fetch.
   * @param {string} property - The property name in state to update.
   */
  getMapData(endpoint, property) {
    fetch(endpoint)
      .then((response) => response.json())
      .then((rawData) => {
        const mapData = new Map();
        rawData.forEach((dataPoint) => {
          mapData.set(dataPoint.id, dataPoint);
        });
        this.setState(
          {
            [property]: rawData,
            [`${property}Map`]: mapData,
          },
          this.updateLoadingStatus
        );
      })
      .catch((error) => {
        this.setState({ error });
      });
  }

  /**
   * A function to get contributions for a specific member.
   *
   * @param {string} memberId - the identifier for the member.
   * @returns {array} Contributions made to the member.
   */
  getMemberContribs(memberId) {
    const {
      companiesMap,
      contributions,
    } = this.state;
    return getContributionsForEntity(
      'member',
      memberId,
      contributions,
      companiesMap);
  }

  /**
   * A function to get contributions from a specific company.
   *
   * @param {string} companyId - the identifier for the company.
   * @returns {array} Contributions made by the company.
   */
  getCompanyContribs(companyId) {
    const { contributions, membersMap } = this.state;
    return getContributionsForEntity(
      'company',
      companyId,
      contributions,
      membersMap);
  }

  /**
   * Click listener to populate the member profile pane.
   *
   * @param {number} memberId - Id number of the member to display.
   */
  handleMemberClick(e, memberId) {
    e.preventDefault();
    const { membersMap } = this.state;
    this.setState({
      activeMember: membersMap.get(memberId),
      activeMemberContribs: this.getMemberContribs(memberId),
    });
  }

  /**
   * Click listener to populate the company profile pane.
   *
   * @param {number} companyId - Id number of the company to display.
   */
  handleCompanyClick(e, companyId) {
    e.preventDefault();
    const { companiesMap } = this.state;
    this.setState({
      activeCompany: companiesMap.get(companyId),
      activeCompanyContribs: this.getCompanyContribs(companyId),
    });
  }

  /**
   * Click listener for sorting the member profile panel table.
   * @param {number|string} cycle - The column for which to sort contributions.
   * @param {string} order - 'ASC' or 'DESC'.
   */
  handleMemberTableSortClick(cycle, order) {
    const activeMemberContribs = { ...this.state.activeMemberContribs };
    const sortedData = sortRowsByField(
      activeMemberContribs.rows,
      cycle,
      order
    );
    activeMemberContribs.rows = sortedData;
    this.setState({ activeMemberContribs });
  }

  /**
   * Click listener for sorting the company profile panel table.
   *
   * @param {number|string} cycle - The column for which to sort contributions.
   * @param {string} order - 'ASC' or 'DESC'.
   */
  handleCompanyTableSortClick(cycle, order) {
    const activeCompanyContribs = { ...this.state.activeCompanyContribs };
    const sortedData = sortRowsByField(
      activeCompanyContribs.rows,
      cycle,
      order
    );
    activeCompanyContribs.rows = sortedData;
    this.setState({ activeCompanyContribs });
  }

  /**
   * A function to update the loading status based on state values.
   */
  updateLoadingStatus() {
    const {
      companies,
      companiesMap,
      contributions,
      featuredCompanies,
      featuredMembers,
      members,
      membersMap,
    } = this.state;

    // If any of the collections are empty, don't update.
    if (0 === companies.length ||
      0 === contributions.length ||
      0 === featuredCompanies.length ||
      0 === featuredMembers.length ||
      0 === members.length
    ) {
      return;
    }

    // Default the member profile pane so it has data in it
    this.setState({
      activeMember: membersMap.get(featuredMembers[0].memberId),
      activeMemberContribs:
        this.getMemberContribs(featuredMembers[0].memberId),
      loading: false,
    });

    // Default the company profile pane so it has data in it
    this.setState({
      activeCompany: companiesMap.get(featuredCompanies[0].companyId),
      activeCompanyContribs:
        this.getCompanyContribs(featuredCompanies[0].companyId),
      loading: false,
    });
  }

  /**
   * @returns {object} Component markup.
   */
  render() {
    const {
      campaignFinanceConfig: {
        imageBase = '',
      } = {},
    } = window;

    const {
      activeCompany,
      activeCompanyContribs,
      activeMember,
      activeMemberContribs,
      companiesMap,
      companies,
      error,
      featuredMembers,
      featuredCompanies,
      loading,
      members,
      membersMap,
    } = this.state;

    if (error) {
      return (
        <div className="campaignFinance campaignFinance__error">
          Unable to load data. Please reload the page and try again.
        </div>
      );
    }

    // Handle loading case.
    if (loading) {
      return (
        <div className="campaignFinance campaignFinance__loading">
          Loading ...
        </div>
      );
    }

    return (
      <div className="campaignFinance">
        <div className="divider" />
        <h2>Lawmakers: Filled & Refilled</h2>
        <div className="section campaignFinance__featured-four">
          {featuredMembers.map((feature) => {
            const {
              description,
              eyebrow,
              imageUrl,
              memberId,
            } = feature;

            return (
              <FeatureCard
                entity={membersMap.get(memberId)}
                handleEntityClick={this.handleMemberClick}
                imageUrl={`${imageBase}/${imageUrl}`}
                key={eyebrow}
                title={eyebrow}
                tileDesc={description}
                type="MEMBER"
              />
            );
          })}
        </div>
        <SearchField
          data={members}
          dataType="members"
          onSelect={this.onSearchSelectMember}
        />
        <Profile
          className="section"
          companiesMap={companiesMap}
          contributions={activeMemberContribs}
          entity={activeMember}
          handleTableSortClick={this.handleMemberTableSortClick}
          imageBase={imageBase}
          type="MEMBER"
        />
        <div className="divider" />
        <h2>Drugmakers: Dispensing Dollars</h2>
        <div className="section campaignFinance__featured-four">
          {featuredCompanies.map((feature) => {
            const {
              description,
              eyebrow,
              imageUrl,
              companyId,
            } = feature;
            return (
              <FeatureCard
                entity={companiesMap.get(companyId)}
                handleEntityClick={this.handleCompanyClick}
                imageUrl={`${imageBase}/${imageUrl}`}
                key={eyebrow}
                title={eyebrow}
                tileDesc={description}
                type="COMPANY"
              />
            );
          })}
        </div>
        <SearchField
          data={companies}
          dataType="companies"
          onSelect={this.onSearchSelectCompany}
        />
        <Profile
          className="section"
          contributions={activeCompanyContribs}
          entity={activeCompany}
          handleTableSortClick={this.handleCompanyTableSortClick}
          imageBase={imageBase}
          type="COMPANY"
        />
      </div>
    );
  }
}

ReactDOM.render(React.createElement(CampaignFinance, null), appElement);
